import { SERP_SHOW, SERP_PLATFORMS, SERP_SORT } from "../../config";
import _ from "lodash";

/**
 * Maps URL platform values to SERP_PLATFORMS objects
 * Handles aliases like "revel" -> "courses" (REVEL)
 * @param {string} platformValue - Platform value from URL
 * @returns {Object|null} SERP_PLATFORMS object or null if not found
 */
export const mapUrlPlatformToSERPPlatform = (platformValue) => {
  if (!platformValue || typeof platformValue !== "string") {
    return null;
  }

  const normalizedValue = platformValue.trim().toLowerCase();

  // Create a map of URL values to SERP_PLATFORMS
  const platformMap = {
    // REVEL mappings
    "revel": SERP_PLATFORMS.REVEL,
    "courses": SERP_PLATFORMS.REVEL,
    
    // ETEXT mappings
    "etext": SERP_PLATFORMS.ETEXT,
    "etexts": SERP_PLATFORMS.ETEXT,
    "pearson etext": SERP_PLATFORMS.ETEXT,
    
    // APPLICATION mappings
    "app": SERP_PLATFORMS.APPLICATION,
    "application": SERP_PLATFORMS.APPLICATION,
    "applications": SERP_PLATFORMS.APPLICATION,
    
    // XL_PLUS mappings
    "xl plus": SERP_PLATFORMS.XL_PLUS,
    "xlplus": SERP_PLATFORMS.XL_PLUS,
    "xl-plus": SERP_PLATFORMS.XL_PLUS,
    
    // MLM mappings
    "mlm": SERP_PLATFORMS.MLM,
    "mylab": SERP_PLATFORMS.MLM,
    "mastering": SERP_PLATFORMS.MLM,
    "mylab & mastering": SERP_PLATFORMS.MLM,
    
    // COLLECTION mappings
    "collection": SERP_PLATFORMS.COLLECTION,
    "collections": SERP_PLATFORMS.COLLECTION,
    "etexts_custom_collection": SERP_PLATFORMS.COLLECTION,
    
    // CUSTOM mapping
    "custom": SERP_PLATFORMS.CUSTOM,
    
    // REVIEWER mapping
    "reviewer": SERP_PLATFORMS.REVIEWER,
    
    // CLASS_TEST mappings
    "class test": SERP_PLATFORMS.CLASS_TEST,
    "beta": SERP_PLATFORMS.CLASS_TEST,
  };

  const mappedPlatform = platformMap[normalizedValue];

  if (!mappedPlatform) {
    console.warn(
      `[Platform Util] Unknown platform value from URL: "${platformValue}". Ignoring.`
    );
    return null;
  }

  return mappedPlatform;
};

/**
 * Parses comma-separated platform string from URL and maps to SERP_PLATFORMS
 * @param {string} platformsString - Comma-separated platform values
 * @returns {Array} Array of SERP_PLATFORMS objects (deduplicated by type)
 */
export const parsePlatformsFromUrl = (platformsString) => {
  if (!platformsString || typeof platformsString !== "string") {
    return [];
  }

  const platformValues = platformsString
    .split(",")
    .map((val) => val.trim())
    .filter((val) => val.length > 0);

  const mappedPlatforms = platformValues
    .map((val) => mapUrlPlatformToSERPPlatform(val))
    .filter((platform) => platform !== null);

  // Deduplicate platforms by their type property
  // This handles cases where the same platform is specified multiple times
  // with different aliases (e.g., "xlplus", "xl-plus", "xl plus" all map to XL_PLUS)
  return _.uniqBy(mappedPlatforms, "type");
};

export const isSelectedShowApplicable = (
  selectedPlatform,
  selectedShow,
  hasInstitution,
  isLMS
) => {
  const showByMap = new Map();
  if (isLMS) {
    showByMap.set("all", [
      SERP_SHOW.AVAILABLE,
      SERP_SHOW.SAMPLES,
      SERP_SHOW.COMING_SOON,
      SERP_SHOW.INSTITUTION,
    ]);
    showByMap.set(SERP_PLATFORMS.REVEL.type, [
      SERP_SHOW.AVAILABLE,
      SERP_SHOW.INSTITUTION,
    ]);
    showByMap.set(SERP_PLATFORMS.ETEXT.type, [
      SERP_SHOW.AVAILABLE,
      SERP_SHOW.INSTITUTION,
    ]);
    showByMap.set(SERP_PLATFORMS.CUSTOM.type, [SERP_SHOW.AVAILABLE]);
    showByMap.set(SERP_PLATFORMS.CLASS_TEST.type, [SERP_SHOW.INSTITUTION]);
    showByMap.set(SERP_PLATFORMS.REVIEWER.type, []);
    showByMap.set(SERP_PLATFORMS.APPLICATION.type, []);
  } else {
    if (hasInstitution) {
      showByMap.set("all", [
        SERP_SHOW.AVAILABLE,
        SERP_SHOW.SAMPLES,
        SERP_SHOW.COMING_SOON,
        SERP_SHOW.INSTITUTION,
      ]);
      showByMap.set(SERP_PLATFORMS.REVEL.type, [
        SERP_SHOW.AVAILABLE,
        SERP_SHOW.SAMPLES,
        SERP_SHOW.COMING_SOON,
        SERP_SHOW.INSTITUTION,
      ]);
      showByMap.set(SERP_PLATFORMS.ETEXT.type, [
        SERP_SHOW.AVAILABLE,
        SERP_SHOW.INSTITUTION,
      ]);
      showByMap.set(SERP_PLATFORMS.CLASS_TEST.type, [SERP_SHOW.INSTITUTION]);
    } else {
      showByMap.set("all", [
        SERP_SHOW.AVAILABLE,
        SERP_SHOW.SAMPLES,
        SERP_SHOW.COMING_SOON,
      ]);
      showByMap.set(SERP_PLATFORMS.REVEL.type, [
        SERP_SHOW.AVAILABLE,
        SERP_SHOW.SAMPLES,
        SERP_SHOW.COMING_SOON,
      ]);
      showByMap.set(SERP_PLATFORMS.ETEXT.type, [SERP_SHOW.AVAILABLE]);
      showByMap.set(SERP_PLATFORMS.CLASS_TEST.type, []);
    }
    showByMap.set(SERP_PLATFORMS.COLLECTION.type, [SERP_SHOW.AVAILABLE]);
    showByMap.set(SERP_PLATFORMS.APPLICATION.type, []);
    showByMap.set(SERP_PLATFORMS.REVIEWER.type, []);
    showByMap.set(SERP_PLATFORMS.CUSTOM.type, [SERP_SHOW.AVAILABLE]);
  }

  const showByItems = showByMap.get(selectedPlatform);
  const tempShowByItem = _.find(showByItems, function (showByItem) {
    return showByItem.type === selectedShow;
  });

  if (tempShowByItem) {
    return true;
  } else {
    return false;
  }
};

export const isSelectedSortApplicable = (selectedPlatform, selectedSort) => {
  const sortByMap = new Map();
  sortByMap.set("all", [
    SERP_SORT.TITLE,
    SERP_SORT.AUTHOR,
    SERP_SORT.PUBLICATION_DATE,
  ]);
  sortByMap.set(SERP_PLATFORMS.REVEL.type, [
    SERP_SORT.TITLE,
    SERP_SORT.AUTHOR,
    SERP_SORT.PUBLICATION_DATE,
  ]);
  sortByMap.set(SERP_PLATFORMS.ETEXT.type, [
    SERP_SORT.TITLE,
    SERP_SORT.PUBLICATION_DATE,
  ]);
  sortByMap.set(SERP_PLATFORMS.CUSTOM.type, [
    SERP_SORT.TITLE,
    SERP_SORT.AUTHOR,
    SERP_SORT.PUBLICATION_DATE,
  ]);
  sortByMap.set(SERP_PLATFORMS.CLASS_TEST.type, [
    SERP_SORT.TITLE,
    SERP_SORT.AUTHOR,
    SERP_SORT.PUBLICATION_DATE,
  ]);
  sortByMap.set(SERP_PLATFORMS.REVIEWER.type, [
    SERP_SORT.TITLE,
    SERP_SORT.AUTHOR,
    SERP_SORT.PUBLICATION_DATE,
  ]);
  sortByMap.set(SERP_PLATFORMS.APPLICATION.type, []);

  const sortByItems = sortByMap.get(selectedPlatform);
  const tempSortByItem = _.find(sortByItems, function (sortByItem) {
    return sortByItem.type === selectedSort;
  });

  if (tempSortByItem) {
    return true;
  } else {
    return false;
  }
};

/**
 * Checks if etext is present in the URL for LMS flow
 * @param {URLSearchParams} queryParams - URL search parameters
 * @returns {string|null} Returns platform type "etexts" if etext is found in LMS flow, null otherwise
 */
export const getEtextPlatformForLMS = (queryParams) => {
  // Check if we're in LMS flow (has integration_context_token)
  const hasIntegrationToken = queryParams && queryParams.has("integration_context_token");
  
  if (!hasIntegrationToken) {
    return null;
  }

  // Check if etext is in platforms parameter
  if (queryParams.has("platforms")) {
    const platformsParam = queryParams.get("platforms");
    const urlPlatforms = parsePlatformsFromUrl(platformsParam);
    const hasEtext = urlPlatforms.some(
      (platform) => platform.type === SERP_PLATFORMS.ETEXT.type
    );
    if (hasEtext) {
      return SERP_PLATFORMS.ETEXT.type;
    }
  }

  // Check if etext is in ref parameter
  if (queryParams.has("ref")) {
    const refValue = queryParams.get("ref").toLowerCase();
    if (refValue === "etext" || refValue === "etexts") {
      return SERP_PLATFORMS.ETEXT.type;
    }
  }

  return null;
};

/**
 * Checks if we're in LMS flow (has integration_context_token)
 * @param {URLSearchParams} queryParams - URL search parameters
 * @returns {boolean} Returns true if in LMS flow, false otherwise
 */
export const isLMSFlow = (queryParams) => {
  if (!queryParams) {
    return false;
  }
  
  // Check if we have integration_context_token (required for LMS flow)
  // The presence of integration_context_token indicates LMS flow
  return queryParams.has("integration_context_token");
};
